/**
 * Next.js App Router can chain hashes on same-path navigations when `href` is a single string
 * (e.g. `/hub#a` then `/hub#b` → `/hub#a#b`). See vercel/next.js#93126.
 */

/** Fragment without leading `#`. If multiple `#` appear (malformed), keep the last segment. */
export function normalizeChainedHashFragment(fragment: string): string {
	if (!fragment.includes('#')) return fragment
	const parts = fragment.split('#').filter(part => part.length > 0)
	return parts[parts.length - 1] ?? ''
}

/**
 * Use with `next/link` for paths that include `#…` so the fragment replaces instead of chaining.
 * `hash` must not include a leading `#`.
 */
export function nextLinkHrefFromPathWithOptionalHash(
	to: string
): string | { pathname: string; hash: string } {
	const hashIdx = to.indexOf('#')
	if (hashIdx === -1) return to
	const pathname = to.slice(0, hashIdx)
	const hash = normalizeChainedHashFragment(to.slice(hashIdx + 1))
	if (!pathname) return to
	if (!hash) return pathname
	return { pathname, hash }
}

/**
 * Safe `router.push` for paths that may include `#…`.
 * Splits the string so Next.js App Router receives a clean path + fragment,
 * avoiding double-hash chaining when the current URL already carries a fragment.
 */
export function pushPathWithHash(
	router: { push: (href: string) => void },
	href: string
): void {
	const result = nextLinkHrefFromPathWithOptionalHash(href)
	if (typeof result === 'string') {
		router.push(result)
	} else {
		router.push(`${result.pathname}#${result.hash}`)
	}
}

/** Rewrites `location` when the fragment wrongly contains `#`, then dispatches `hashchange`. */
export function canonicalizeWindowLocationHash(): void {
	if (typeof window === 'undefined') return
	const raw = window.location.hash.slice(1)
	if (!raw.includes('#')) return
	const normalized = normalizeChainedHashFragment(raw)
	if (!normalized) return
	const path = `${window.location.pathname}${window.location.search}`
	window.history.replaceState(window.history.state, '', `${path}#${normalized}`)
}
